import { Box, Button, Stack, Tab, Tabs } from '@mui/material'
import { ICellRendererParams } from 'ag-grid-community'
import { createCode, getCodes, saveDraftCode, updateCode } from 'api/accessCode'
import { useAuthContext } from 'components/context/AuthProvider'
import NotificationSys from 'components/NotificationSystem'
import InfinityAgGrid from 'components/table/InfinityAgGrid'
import TableToolbar from 'components/table/TableToolbar'
import { getTitle } from 'components/tabs/TabsTitle'
import { getActionColumn } from 'features/UI'
import { useExportCSV } from 'hooks/useExportCSV'
import { useGridControl } from 'hooks/useGridControl'
import { useShowControl } from 'hooks/useShowControl'
import { useTabsControl } from 'hooks/useTabsControl'
import { useCallback, useMemo, useState } from 'react'
import { useMutation } from 'react-query'
import {
  AccessCode,
  BaseTableRequest,
  EAccessCodeOwnership,
  EAccessCodeStatus,
  EAccessCodeType,
  EOp,
  FilterModel,
  Nullish,
} from 'types'
import { useGetDataSource } from 'utils'
import { useAccessCodeRowStyle } from '../../hooks/getAccessCodeRowStyle'
import { useAccessCodeStatusFilter } from '../../hooks/useAccessCodeStatusFilter'
import { AccessCodeGenerationModal } from '../AccessCodeGenerationModal/AccessCodeGenerationModal'
import { PartnerCodesTab } from '../PartnerCodesTab/PartnerCodesTab'
import {
  csvInstitutionalCodeColumns,
  csvPromoCodeColumns,
  institutionalCodeColumns,
  promoCodeColumns,
} from './csvColumns'

// Partner codes are a separate entity, not an access-code type
const PARTNER_CODES_TAB = 'partnerCodes'
type AccessManagementTab = EAccessCodeType | typeof PARTNER_CODES_TAB

export const AccessManagement = () => {
  const { selectedTab, handleChangeSelectedTab } = useTabsControl<AccessManagementTab>(
    EAccessCodeType.institutionAccessCode,
  )

  const { isAdmin } = useAuthContext()

  const [isOpenGenerate, handleOpenGenerate, handleCloseGenerate] = useShowControl()
  const [isEdit, handleOpenEdit, handleCloseEdit] = useShowControl()
  const [editedItem, setEditedItem] = useState<Nullish<AccessCode>>(null)

  const openGenerateNew = useCallback(() => {
    handleOpenGenerate()
    setEditedItem(null)
  }, [handleOpenGenerate])

  const handleClickEdit = useCallback(
    (_: number, item: ICellRendererParams<AccessCode>) => {
      if (!item.data) return

      if (item.data.status === EAccessCodeStatus.draft) {
        handleOpenGenerate()
        setEditedItem(item.data)
      } else {
        setEditedItem(item.data)
        handleOpenEdit()
      }
    },
    [handleOpenEdit, handleOpenGenerate],
  )

  const columns = useMemo(() => {
    if (selectedTab === EAccessCodeType.institutionAccessCode) {
      let columns = [...institutionalCodeColumns]
      if (isAdmin) {
        columns = columns.concat(
          getActionColumn([
            {
              name: 'Edit',
              onClick: handleClickEdit,
              // Individual codes have no organization, so the edit form — which requires
              // organization fields — cannot save them.
              hideCondition: (props: ICellRendererParams<AccessCode>) =>
                props.data?.ownership === EAccessCodeOwnership.individual,
            },
          ]),
        )
      }
      return columns
    }

    if (selectedTab === EAccessCodeType.promoCode) {
      return promoCodeColumns
    }

    return []
  }, [handleClickEdit, isAdmin, selectedTab])

  const { StatusFilter, statusFilterValue } = useAccessCodeStatusFilter()

  const getCodeWrapper = useCallback(
    (request: BaseTableRequest) => {
      const extendFilter: FilterModel = {
        ...request.filter,
        codeType: {
          type: 'text',
          op: EOp.equals,
          value: selectedTab,
        },
      }

      if (extendFilter.organizationName) {
        extendFilter['or'] = [
          { ['organizationName']: extendFilter.organizationName },
          { ['organization.name']: extendFilter.organizationName },
        ]

        delete extendFilter.organizationName
      }

      if (statusFilterValue && selectedTab === EAccessCodeType.institutionAccessCode) {
        extendFilter.status = statusFilterValue
      }

      return getCodes({
        ...request,
        filter: extendFilter,
      })
    },
    [selectedTab, statusFilterValue],
  )

  const getDataSource = useGetDataSource(getCodeWrapper)
  const { gridApi, onGridReady, columnApi } = useGridControl(getDataSource)

  const mutation = useMutation(createCode, {
    onSuccess: (data) => {
      handleCloseGenerate()
      gridApi?.purgeInfiniteCache()
      NotificationSys.showSuccess(`Created access code ${data.data.code}`)
    },
  })

  const saveDraftMutation = useMutation(saveDraftCode, {
    onSuccess: (data) => {
      handleCloseGenerate()
      gridApi?.purgeInfiniteCache()
      NotificationSys.showSuccess(`Draft access code ${data.data.code} saved`)
    },
  })

  const onCloseEdit = useCallback(() => {
    setEditedItem(null)
    handleCloseEdit()
  }, [handleCloseEdit])

  const updateMutation = useMutation(updateCode, {
    onSuccess: (data) => {
      onCloseEdit()
      gridApi?.purgeInfiniteCache()
      NotificationSys.showSuccess(`Updated access code ${data.data.code}`)
    },
  })

  const { csvLoading, onExportCSV } = useExportCSV(
    getCodeWrapper,
    gridApi,
    columnApi,
    selectedTab === EAccessCodeType.institutionAccessCode
      ? csvInstitutionalCodeColumns
      : csvPromoCodeColumns,
    `${selectedTab} Access Codes.csv`,
  )

  const getRowStyle = useAccessCodeRowStyle()

  const left = (
    <>
      <Tabs value={selectedTab} onChange={handleChangeSelectedTab} indicatorColor="primary">
        <Tab
          value={EAccessCodeType.institutionAccessCode}
          label={getTitle('Institutional Access Codes')}
        />
        <Tab value={EAccessCodeType.promoCode} label={getTitle('Promo Codes')} />
        <Tab value={PARTNER_CODES_TAB} label={getTitle('Partner Codes')} />
      </Tabs>
    </>
  )

  if (selectedTab === PARTNER_CODES_TAB) {
    return <PartnerCodesTab left={left} />
  }

  return (
    <>
      <TableToolbar
        exportCSVEnable={true}
        left={left}
        onExportCSV={onExportCSV}
        csvLoading={csvLoading}
      >
        {isAdmin && selectedTab !== EAccessCodeType.promoCode ? (
          <Button variant="outlined" sx={{ minWidth: 120 }} onClick={openGenerateNew}>
            Generate Code
          </Button>
        ) : null}
      </TableToolbar>

      {selectedTab === EAccessCodeType.institutionAccessCode && (
        <Stack flexDirection="row" alignItems="center" mt={1} mb={2} className="gap-16">
          <Box minWidth="280px">{StatusFilter}</Box>
        </Stack>
      )}

      <InfinityAgGrid
        pagination
        columnDefs={columns}
        getRowStyle={getRowStyle}
        onGridReady={onGridReady}
      />

      <AccessCodeGenerationModal
        onSaveDraft={saveDraftMutation.mutate}
        loading={mutation.isLoading}
        isOpen={isOpenGenerate}
        handleClose={handleCloseGenerate}
        onGenerate={mutation.mutate}
        onUpdate={updateMutation.mutate}
        type={selectedTab}
        editedItem={editedItem}
      />

      <AccessCodeGenerationModal
        onSaveDraft={saveDraftMutation.mutate}
        loading={updateMutation.isLoading}
        isOpen={isEdit}
        handleClose={onCloseEdit}
        onGenerate={mutation.mutate}
        onUpdate={updateMutation.mutate}
        type={selectedTab}
        editedItem={editedItem}
      />
    </>
  )
}
